import { proxy } from 'valtio';

export enum FeatureFlagSource {
  /** Flags used by our code but not managed by LD or Statsig */
  Local = 'local',
  /** Flags managed by Statsig */
  Statsig = 'statsig',
}

export enum TargetApp {
  CanvasWeb = 'canvas_web',
  DatabasesWeb = 'cc-databases_web',
}

export type ClientFeatureFlagName = keyof typeof defaultFeatureFlagState;
export const isFeatureFlagName = (flag: string): flag is ClientFeatureFlagName => {
  return flag in defaultFeatureFlagState;
};

export type ClientFeatureFlag =
  | {
      enabled: boolean;
      source: FeatureFlagSource.Local;
      targetApp: TargetApp[];
    }
  | {
      enabled: boolean;
      flag: string;
      source: FeatureFlagSource.Statsig;
      targetApp: TargetApp[];
    };
export type ClientFeatureFlags = Record<ClientFeatureFlagName, ClientFeatureFlag>;
/**
 * All feature flags must be disabled by default.
 * @description Default feature flag values cannot be mutated.
 *
 * @remark: You can locally override these values in your development environment by:
 * - adding them to .ff-localdev, which will persist across app restarts
 * - adding the query param ?df=<flagName> to the URL, which works in dev and staging
 * - using the dev-tools panel in local dev
 */
export const defaultFeatureFlagState = {
  /**
   * This flag enables/disables hard breaks in the canvas editor
   */
  canvasEditorHardBreak: {
    enabled: true,
    source: FeatureFlagSource.Local,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * This flag enables/disables the use of style resolution in the render manager.
   * https://switcheroo.atlassian.com/ui/gates/ae78b1c5-d387-4d42-ad0b-1843818f6378/key/use-style-resolution-in-render-manager
   */
  useStyleResolutionInRenderManager: {
    enabled: false,
    flag: 'use-style-resolution-in-render-manager',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Used for testing purposes. Do not use otherwise.
   * @deprecated
   */
  test: {
    enabled: false,
    source: FeatureFlagSource.Local,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Holding shift while dragging will use snap to grid instead of the weird
   * snapping logic we currently have which locks the X and Y on drag
   * https://switcheroo.atlassian.com/ui/gates/847b94df-c70b-429d-bc65-0e4db3572485
   */
  snapToGrid: {
    enabled: false,
    source: FeatureFlagSource.Statsig,
    flag: 'cc-whiteboards-enable-snap-to-grid-using-shift',
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables native media files (video, audio, documents, archives, data)
   * in Whiteboards. When OFF, the @canvas/module-media module reports
   * isEnabled=false so it is not loaded into any mode and no wiring
   * (element-type registration, importer routing, clipboard predicates,
   * file-picker accept lists) takes effect at runtime.
   *
   * Pre-existing image and PDF behaviour is unaffected.
   *
   * TODO: add statsig flag
   */
  whiteboardsNativeMedia: {
    enabled: false,
    source: FeatureFlagSource.Local,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables new mechanism of submitting paywall events to AFM
   * https://switcheroo.atlassian.com/ui/gates/28018e6b-d436-421a-bdab-f97e0c0b5c31/key/cc-whiteboards-paywall-events-through-rovo-bridge
   */
  enableNewPaywallBridge: {
    enabled: false,
    source: FeatureFlagSource.Statsig,
    flag: 'cc-whiteboards-paywall-events-through-rovo-bridge',
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables adding debug report to sentry errors
   */
  shouldAddDebugReportToSentryErrors: {
    enabled: false,
    source: FeatureFlagSource.Local,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Change the url we call from the client to stargate to make use of the new
   * archetype stargate route
   * https://switcheroo.atlassian.com/gates/88ccf24a-d511-4a42-b627-9e77befa9d59
   */
  stargateRoutingUseArchetype: {
    enabled: true,
    flag: 'stargate_routing_use_archetype',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Align elements flag to enable aligning elements as a feature
   */
  alignElements: {
    enabled: false,
    flag: 'cc-whiteboards-align-elements',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Turns on using zoom CSS field in the underlay system (better layout
   * calculations) rather than transforms
   */
  useZoomCssInUnderlaySystem: {
    enabled: false,
    source: FeatureFlagSource.Local,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * A Feature Flag for exposing hyperlinks
   * https://switcheroo.atlassian.com/ui/gates/fb48f68f-c5b5-4895-8601-0961d14d8076
   */
  hyperlinks: {
    enabled: true,
    flag: 'should-enable-hyperlinks',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Uses explicit SPA navigation refactor instead of relying on token change.
   *
   */
  explicitSPANavigation: {
    enabled: false,
    source: FeatureFlagSource.Local,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables the AI Smart Button and connection to the Confluence right panel rendering agent capabilities
   * https://switcheroo.atlassian.com/ui/gates/fa7497cc-b87f-483b-b6ea-e1457671c9f0/key/should-enable-whiteboard-ai-smart-button
   */
  aiSmartButton: {
    enabled: false,
    flag: 'should-enable-whiteboard-ai-smart-button',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables v2 permissions infrastructure using policies.
   * https://switcheroo.atlassian.com/ui/gates/608631c1-d60f-4030-bba9-2c40a115b6d4
   */
  v2Permissions: {
    enabled: false,
    flag: 'should-use-v2-permissions',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables v2 permissions infrastructure using policies for reads.
   * https://switcheroo.atlassian.com/ui/gates/1ee0ee5f-7f0f-4878-a120-6ad283799cd9/key/should-use-v2-permissions-on-reads-from-policies
   */
  v2PermissionsReadsFromPolicies: {
    enabled: false,
    flag: 'should-use-v2-permissions-on-reads-from-policies',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enabled a low quality mode if only software rendering is available
   * https://console.statsig.com/LqivKg6ADZZaGczRfBKfX/gates/whiteboard-should-enable-low-quality-mode
   */
  lowQualityMode: {
    enabled: false,
    flag: 'whiteboard-should-enable-low-quality-mode',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables the "retry" button for AI clustering/smart grouping feature.
   *
   * Should only be enabled once AI clustering supports responding with a
   * different cluster on each request
   */
  aiClusteringRetry: {
    enabled: false,
    flag: 'should_enable_ai_clustering_retry_button',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Makes it so that section titles remain the same size regardless of the
   * camera scale
   */
  cameraAgnosticSectionTitles: {
    enabled: false,
    source: FeatureFlagSource.Local,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables the whiteboards card element
   * https://switcheroo.atlassian.com/ui/gates/f2c97645-5a28-422d-83aa-fc7c59c45e71/key/should-enable-whiteboard-card
   */
  cardElement: {
    enabled: false,
    flag: 'should-enable-whiteboard-card',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables the accessibility module containing canvas as DOM, and other a11y features.
   * https://switcheroo.atlassian.com/ui/gates/aa2aba2d-0060-4b20-86e9-d3d44184369d/key/a11y-module
   */
  a11yModule: {
    enabled: false,
    flag: 'a11y-module',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables memory usage emitting
   * https://switcheroo.atlassian.com/ui/gates/ce4ab93c-604d-43ee-a363-20e181bedfed/key/whiteboards_should_emit_frontend_memory_metrics
   */
  shouldEmitFrontendMemoryMetrics: {
    enabled: false,
    flag: 'whiteboards_should_emit_frontend_memory_metrics',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables the locale switcher, which is ONLY for whiteboards developers.
   * https://switcheroo.atlassian.com/ui/gates/ba151294-55cc-49c9-8b2a-af2f65e9a42e/key/enable_whiteboards_locale_switcher
   */
  enableWhiteboardsLocaleSwitcher: {
    enabled: false,
    flag: 'enable_whiteboards_locale_switcher',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables the 'Find and Replace' feature in whiteboards.
   * https://switcheroo.atlassian.com/ui/gates/cc29b2d5-734c-43d3-af90-c47d3468780b/key/whiteboards_find_and_replace
   */
  whiteboardsFindAndReplace: {
    enabled: true,
    flag: 'whiteboards_find_and_replace',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables the visual refresh for atlaskit components and colors
   *
   * NOTE: This is NOT a canvas specific feature gate, this gate is used by
   * multiple products and teams. Take EXTREME CARE when updating this flag.
   *
   * https://switcheroo.atlassian.com/ui/gates/05b28d4c-67f1-45db-a0c6-4c14ec40185c/key/platform-component-visual-refresh
   */
  visualRefreshComponentsAndColors: {
    enabled: false,
    flag: 'platform-component-visual-refresh',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables the visual refresh for platform icons.
   *
   * NOTE: This is NOT a canvas specific feature gate, this gate is used by
   * multiple products and teams. Take EXTREME CARE when updating this flag.
   *
   * https://switcheroo.atlassian.com/ui/gates/e36c4a44-2846-4b76-b262-40e1d1e3932e/key/platform-visual-refresh-icons
   */
  visualRefreshIcons: {
    enabled: true,
    flag: 'platform-visual-refresh-icons',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Whether or not to proceed with using the dirty status of the whiteboard, and whether or not to proceed with attempting to properly clean up the dirty board
   * https://switcheroo.atlassian.com/ui/gates/aa6287b7-6739-445d-b312-06ae6446f3e2/key/clean_dirty_whiteboard
   */
  cleanDirtyWhiteboard: {
    enabled: false,
    flag: 'clean_dirty_whiteboard',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /*
   * Enables content wrapper - header in confluence
   * https://switcheroo.atlassian.com/ui/gates/86ef3e79-79a9-4799-b061-9e7f0cf3f36b/key/confluence_frontend_object_header
   */
  confluenceContentWrapperHeader: {
    enabled: false,
    flag: 'confluence_frontend_object_header',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Exposes mindmaps as a feature
   * https://switcheroo.atlassian.com/ui/gates/659f56de-ea80-482e-a9f2-ae96b90e0fea/key/should_enable_mindmaps
   */
  mindmaps: {
    enabled: false,
    flag: 'should_enable_mindmaps',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Should it be possible to stack sections within sections - this modifies sections to follow
   * the same logic as other element types.
   */
  stackableSections: {
    enabled: false,
    flag: 'confluence-whiteboards-stackable-sections',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  devSocket: {
    enabled: false,
    flag: 'dev_socket_disconnect',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Updates media field settings for Field Settings 2.0 project
   *
   * https://switcheroo.atlassian.com/ui/gates/f5ff837f-5fd3-4049-9775-7af3c276b4fd/key/database-field-setting-2-media
   */
  mediaFieldSettings: {
    enabled: false,
    flag: 'database-field-setting-2-media',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables throttling of awareness updates for selection and transient transaction channels
   * based on the number of collaborators
   *
   * https://switcheroo.atlassian.com/ui/gates/1dd2d683-40b7-4d82-8bcf-26e5e9aeb20a/key/canvas_throttle_awareness_updates
   */
  throttleAwarenessUpdates: {
    enabled: true,
    flag: 'canvas_throttle_awareness_updates',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Allows users to switch to an inline view for smart links
   */
  inlineViewForSmartLinks: {
    enabled: true,
    source: FeatureFlagSource.Local,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables the use of content wrapper refresh in confluence
   * See: https://hello.atlassian.net/wiki/spaces/CAN/pages/5048306970/Feature+Content+wrapper+for+whiteboards#%F0%9F%96%BC%EF%B8%8F-The-feature
   *
   * https://switcheroo.atlassian.com/ui/gates/d4632be3-73c3-4ab2-babb-0ae7d7d07f19/key/should_use_content_wrapper_refresh
   * See https://switcheroo.atlassian.com/ui/gates/ece5dbb1-5afd-4666-8702-8f346a8731f7/key/confluence_frontend_content_wrapper
   */
  shouldUseContentWrapperRefresh: {
    enabled: true,
    source: FeatureFlagSource.Local,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables the pen tool
   * https://switcheroo.atlassian.com/ui/gates/b8481e8a-5982-49ae-a78a-efae264c54f5/key/should_enable_pen
   */
  pen: {
    enabled: false,
    flag: 'should_enable_pen',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables frame throttling for underlay dom elements like smart links
   */
  domFrameThrottling: {
    enabled: true,
    source: FeatureFlagSource.Local,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables switcheroo dev ui for creating/inspecting/updating health of experiments
   */
  switcherooObserver: {
    enabled: false,
    source: FeatureFlagSource.Local,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables consolidated comments
   *
   * https://switcheroo.atlassian.com/ui/gates/996d045e-c24d-4b68-a73a-2eac3dae01a2/key/cc-whiteboards-consolidating-comments
   */
  consolidatedComments: {
    enabled: true,
    source: FeatureFlagSource.Local,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables the media for comments
   *
   * https://switcheroo.atlassian.com/ui/gates/dcc431e8-ddbc-4ba4-bda0-07d8ed0a07e9/key/cc-whiteboards-consolidating-comments-media
   */
  commentsMedia: {
    enabled: false,
    flag: 'cc-whiteboards-consolidating-comments-media',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * https://switcheroo.atlassian.com/ui/gates/05649fc3-e17a-4848-9e4f-a2c52c057485/key/cc-whiteboards-consolidating-comments-unread
   */
  unreadComments: {
    enabled: false,
    flag: 'cc-whiteboards-consolidating-comments-unread',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables the diagramming agent
   * https://switcheroo.atlassian.com/ui/gates/7a062391-adbd-427c-9b4c-3243921847b3/key/diagramming_agent_for_whiteboards
   */
  diagrammingAgent: {
    enabled: false,
    flag: 'diagramming_agent_for_whiteboards',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Feature flag for #hot-120681
   * https://switcheroo.atlassian.com/ui/gates/03cae3dc-47a5-4315-9ffc-905d4fdca210/key/whiteboards_connection_fallback
   */
  useConnectionFallback: {
    enabled: false,
    flag: 'whiteboards_connection_fallback',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb, TargetApp.DatabasesWeb],
  },
  /**
   * Enables lazy sync during connection flow, don't need to do handshake immediately
   * https://switcheroo.atlassian.com/ui/gates/68fc5cf4-8646-4ed9-a25a-31282fa5faa1/key/canvas_lazy_sync_handshake
   */
  lazySyncHandshake: {
    enabled: false,
    flag: 'canvas_lazy_sync_handshake',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb, TargetApp.DatabasesWeb],
  },
  /**
   * Enables the new look for the main toolbar for content wrapper work
   * https://switcheroo.atlassian.com/ui/gates/da643c23-584e-4d7e-bf7b-ced0c5cbca78/key/content_wrapper_main_toolbar_refresh
   * Fully rolled out to all users. The default is set to true until the old toolbar is removed.
   * See https://switcheroo.atlassian.com/ui/gates/ece5dbb1-5afd-4666-8702-8f346a8731f7/key/confluence_frontend_content_wrapper
   */
  contentWrapperMainToolbarRefresh: {
    enabled: true,
    source: FeatureFlagSource.Local,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Uses Convo AI endpoint for AI Stickies
   * https://switcheroo.atlassian.com/ui/gates/563d5403-1387-4cff-a6f7-f8ec80b56892/key/migrate_ai_teammate_to_convo_ai
   */
  migrateAiTeammateToConvoAI: {
    enabled: false,
    flag: 'migrate_ai_teammate_to_convo_ai',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables v2 brainstorming agent API
   * https://switcheroo.atlassian.com/ui/gates/3cd9c598-4cf2-4ea3-b28c-f477b5cccbb7/key/should_use_v2_brainstorming_agent_api
   */
  useV2BrainstormingAgentAPI: {
    enabled: false,
    flag: 'should_use_v2_brainstorming_agent_api',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Force enables the dev UI
   * https://switcheroo.atlassian.com/ui/gates/1f9f2cf9-b77c-43b9-856a-89ab91a6e83a/key/whiteboards_dev_ui
   */
  forceEnableDevUI: {
    enabled: false,
    flag: 'whiteboards_dev_ui',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables the new element toolbar changes.
   * https://switcheroo.atlassian.com/ui/gates/c5ff782f-5c96-495d-a597-e23cbb481219/key/cc-whiteboards-new-element-toolbar
   */
  newElementToolbar: {
    enabled: false,
    flag: 'cc-whiteboards-new-element-toolbar',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Native Database embed milestone flags
   * These flags correspond to the milestones defined in the Database Visual Refresh project.
   */

  /**
   * M1b Hello release: Toolbar/Action Bar
   * Covers:
   * - Database search filtering
   * - Embed settings panel
   * - Sort panel
   * - Filter panel
   * - Hide fields panel
   * - Database templates & import configuration
   * - WBs Element Toolbar → DBs Action Bar: Cell Interactions
   * - View/Change views from element toolbar
   * - Layout panel
   *
   * @see https://hello.atlassian.net/wiki/spaces/CD2/pages/4737992889/Feature+New+native+embed+whiteboards+pages
   */
  wbDbToolbarActionBar: {
    targetApp: [TargetApp.CanvasWeb],
    source: FeatureFlagSource.Local,
    enabled: false,
  },

  /**
   * M1c: Hello release: Flags/Snackbars/Modals
   * Covers:
   * - Snackbars shown in Whiteboards location
   * - Save changes button on DB embed
   * - Flags/section messages shown in Whiteboards location
   */
  wbDbFlagsSnackbarsModals: {
    targetApp: [TargetApp.CanvasWeb],
    source: FeatureFlagSource.Local,
    enabled: false,
  },

  /**
   * M1d: Hello release: Other
   * Covers:
   * - Best effort initial and dynamic sizing
   * - E2E tests, performance tests, SLOs, integration tests
   * - Value & row entries overhead
   * - Create empty database from Whiteboards
   * - Experimentation analytics
   * @see https://hello.atlassian.net/wiki/spaces/CD2/pages/4737992889/Feature+New+native+embed+whiteboards+pages
   */
  wbDbHelloReleaseOther: {
    targetApp: [TargetApp.CanvasWeb],
    source: FeatureFlagSource.Local,
    enabled: false,
  },

  /**
   * M1e: Customer release: Change-boarding
   * Covers:
   * - 'New' indicator on databases element in whiteboards toolbar
   * - User awareness of new functionality
   * @see https://hello.atlassian.net/wiki/spaces/CD2/pages/4737992889/Feature+New+native+embed+whiteboards+pages
   */
  wbDbChangeboarding: {
    targetApp: [TargetApp.CanvasWeb],
    source: FeatureFlagSource.Local,
    enabled: false,
  },
  /**
   * Enables ECS loop performance monitoring and reporting to UFO
   * https://switcheroo.atlassian.com/ui/gates/8c00a7c0-f780-483b-b823-3e6ceca37e70/key/cc-whiteboards-ecs-loop-perf
   */
  ecsLoopPerf: {
    source: FeatureFlagSource.Statsig,
    enabled: false,
    flag: 'cc-whiteboards-ecs-loop-perf',
    targetApp: [TargetApp.CanvasWeb],
  },
  'platform-visual-refresh-icons': {
    source: FeatureFlagSource.Statsig,
    enabled: false,
    flag: 'platform-visual-refresh-icons',
    targetApp: [TargetApp.DatabasesWeb],
  },
  'databases-frontend-ci2-m1': {
    source: FeatureFlagSource.Statsig,
    enabled: true,
    flag: 'databases-frontend-ci2-m1',
    targetApp: [TargetApp.DatabasesWeb],
  },
  'databases-frontend-live-doc': {
    source: FeatureFlagSource.Statsig,
    enabled: false,
    flag: 'databases-frontend-live-doc',
    targetApp: [TargetApp.DatabasesWeb],
  },
  'platform-component-visual-refresh': {
    source: FeatureFlagSource.Statsig,
    enabled: true,
    flag: 'platform-component-visual-refresh',
    targetApp: [TargetApp.DatabasesWeb],
  },
  'databases-rovo-referencing-content': {
    source: FeatureFlagSource.Statsig,
    enabled: false,
    flag: 'confluence_frontend_rovo_db_referencing_content',
    targetApp: [TargetApp.DatabasesWeb],
  },
  'canvas-issue-terminology-refresh-update': {
    source: FeatureFlagSource.Statsig,
    enabled: false,
    flag: 'canvas-issue-terminology-refresh-update',
    targetApp: [TargetApp.CanvasWeb, TargetApp.DatabasesWeb],
  },
  'canvas-project-terminology-refresh': {
    source: FeatureFlagSource.Statsig,
    enabled: false,
    flag: 'canvas-project-terminology-refresh',
    targetApp: [TargetApp.CanvasWeb, TargetApp.DatabasesWeb],
  },
  /**
   * Enables loading field options per issue type in section actions, so that
   * custom fields restricted to specific issue type contexts (e.g. Epic-only
   * fields) return their configured options.
   */
  'canvas-section-actions-context-aware-field-options': {
    source: FeatureFlagSource.Statsig,
    enabled: false,
    flag: 'canvas-section-actions-context-aware-field-options',
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables flags to be displayed over the bridge API.
   * https://switcheroo.atlassian.com/ui/gates/9bd09e19-971f-442b-abd4-289d0f8fe4a2/key/database-frontend-flags-over-bridge
   */
  'database-frontend-flags-over-bridge': {
    source: FeatureFlagSource.Statsig,
    enabled: false,
    flag: 'database-frontend-flags-over-bridge',
    targetApp: [TargetApp.DatabasesWeb],
  },
  'databases-collab-use-canvas-tenant-context-route': {
    source: FeatureFlagSource.Statsig,
    enabled: false,
    flag: 'databases-collab-use-canvas-tenant-context-route',
    targetApp: [TargetApp.DatabasesWeb],
  },
  whiteboardsShowAllDiagramTypes: {
    source: FeatureFlagSource.Statsig,
    enabled: false,
    flag: 'whiteboards-show-all-diagram-types',
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * The new approach for updating an element instead of deleting + creating a
   * new one.
   * https://switcheroo.atlassian.com/ui/gates/4c543991-abac-4b9a-916b-f24bf9cb79c1/key/whiteboards_aifc_transform
   */
  whiteboardsAIFCNewTransform: {
    source: FeatureFlagSource.Statsig,
    enabled: false,
    flag: 'whiteboards_aifc_transform',
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables AIFC edit support for Jira issue elements represented as smart-link
   * backed SVG rects.
   */
  whiteboardsAIFCIssueSupport: {
    source: FeatureFlagSource.Statsig,
    enabled: false,
    flag: 'whiteboards_aifc_issue_support',
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Umbrella flag for short-lived Whiteboards hotfixes (e.g. retrying
   * transient 503s on `updateContentTitle`). Enabling this turns on the
   * additional resilience paths gated by it.
   * https://switcheroo.atlassian.com/ui/gates/b4a4f0f1-cfdd-48fc-90da-1cf5d0d7eb98/key/team_26_whiteboards_hotfixes
   */
  team26WhiteboardsHotfixes: {
    source: FeatureFlagSource.Statsig,
    enabled: false,
    flag: 'team_26_whiteboards_hotfixes',
    targetApp: [TargetApp.CanvasWeb, TargetApp.DatabasesWeb],
  },
  /**
   * A pseudo-feature-flag/segment that is true for Fabric Team Members
   * production/staging and false for everyone else. Designed to be used in
   * tandem with other feature flags.
   * https://switcheroo.atlassian.com/ui/gates/cb44ff26-177b-4c03-849c-b9447ec61881/key/cc-whiteboards-internal-users
   */
  internalUsers: {
    source: FeatureFlagSource.Statsig,
    enabled: false,
    flag: 'cc-whiteboards-internal-users',
    targetApp: [TargetApp.CanvasWeb, TargetApp.DatabasesWeb],
  },
  /**
   * Enables temporary frontend-side Sentry logging of raw LLM output for the
   * canvas-testing-bot debug path.
   * https://switcheroo.atlassian.com/ui/gates/0912fb10-8c61-463e-b14b-3744c3a4fa7d/key/_debug_llm_output_frontend
   */
  debugLlmOutputFrontend: {
    source: FeatureFlagSource.Statsig,
    enabled: false,
    flag: '_debug_llm_output_frontend',
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables features for GA release of the digital teammate agents
   * https://switcheroo.atlassian.com/ui/gates/bec5ece3-8235-44e8-8a6f-18e6a3605e6f/key/digital_teammate_ga
   */
  digitalTeammateGA: {
    source: FeatureFlagSource.Statsig,
    enabled: false,
    flag: 'digital_teammate_ga',
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables native embed bridge initialization.
   * https://switcheroo.atlassian.com/ui/gates/b4c59c3e-a473-43c6-b576-0d560568a263/key/database-frontend-native-embed-bridge
   */
  'database-frontend-native-embed-bridge': {
    source: FeatureFlagSource.Statsig,
    enabled: false,
    flag: 'database-frontend-native-embed-bridge',
    targetApp: [TargetApp.DatabasesWeb],
  },
  'cc-whiteboards-new-element-toolbar': {
    source: FeatureFlagSource.Statsig,
    enabled: false,
    flag: 'cc-whiteboards-new-element-toolbar',
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables updated UI for ADS lozenge, tag, and badge components.
   *
   * NOTE: This is NOT a canvas specific feature gate, this gate is used by
   * multiple products and teams. Take EXTREME CARE when updating this flag.
   *
   * https://switcheroo.atlassian.com/ui/gates/195b5837-cf3f-4b7b-acf6-f755156b849a/key/platform-dst-lozenge-tag-badge-visual-uplifts
   */
  platformLozengeTagBadgeVisualUplifts: {
    source: FeatureFlagSource.Statsig,
    enabled: false,
    flag: 'platform-dst-lozenge-tag-badge-visual-uplifts',
    targetApp: [TargetApp.CanvasWeb, TargetApp.DatabasesWeb],
  },
  /**
   * Atlaskit platform flag that enables native Escape key handling in PopupSelect components.
   * Without this flag, pressing Escape while a PopupSelect dropdown is open (e.g. field picker,
   * sort dropdown) would close the entire contextual menu instead of just the dropdown.
   * Works in conjunction with the `shouldPreventEscapePropagation` prop on PopupSelect.
   */
  platform_navx_sllv_dropdown_escape_and_focus_fix: {
    source: FeatureFlagSource.Statsig,
    enabled: true,
    flag: 'platform_navx_sllv_dropdown_escape_and_focus_fix',
    targetApp: [TargetApp.DatabasesWeb],
  },
  /**
   * Enables the shape token theme by default.
   *
   * NOTE: This is NOT a canvas specific feature gate, this gate is used by
   * multiple products and teams. Take EXTREME CARE when updating this flag.
   *
   * https://switcheroo.atlassian.com/ui/gates/7d3cc87f-78fe-4ed1-a528-69b2f6eb937f/key/platform-dst-shape-theme-default
   */
  platformShapeThemeDefault: {
    source: FeatureFlagSource.Statsig,
    enabled: false,
    flag: 'platform-dst-shape-theme-default',
    targetApp: [TargetApp.CanvasWeb, TargetApp.DatabasesWeb],
  },
  /**
   * Smart Link feature flags required for removing patch -
   * https://switcheroo.atlassian.com/ui/gates/2aed53b3-6766-4834-aafb-15ac38fe9586/key/platform_linking_enable_card_preview_action_size
   * https://switcheroo.atlassian.com/ui/gates/f39f84c4-01ec-43a4-a0c1-3b6de5930ce1/key/linking_platform_hide_preview_action_blanket_prop
   * Setting these to local as we would never want these flags to be disabled in canvas.
   * The purpose of these flags is to turn on the smart link preview action size and hidden blanket props in the smart link component -
   * these props are not used by any other known product, hence these flags in case other products want to use them.
   * (turning these on would result in an strange experience with the blanket only applying to the canvas iframe)
   */
  platform_linking_enable_card_preview_action_size: {
    source: FeatureFlagSource.Local,
    enabled: true,
    targetApp: [TargetApp.DatabasesWeb, TargetApp.CanvasWeb],
  },
  linking_platform_hide_preview_action_blanket_prop: {
    source: FeatureFlagSource.Local,
    enabled: true,
    targetApp: [TargetApp.DatabasesWeb, TargetApp.CanvasWeb],
  },
  platform_deprecate_lp_cc_embed: {
    enabled: false,
    flag: 'platform_deprecate_lp_cc_embed',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.DatabasesWeb, TargetApp.CanvasWeb],
  },
  platform_native_embeds_full_rollout_bugfix: {
    enabled: false,
    flag: 'platform_native_embeds_full_rollout_bugfix',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.DatabasesWeb],
  },
  /**
   * Enables the link picker element toolbar action
   * https://switcheroo.atlassian.com/ui/gates/fdc68cc3-c520-442b-86a5-39ca237551d1/key/whiteboards-use-link-picker-element-toolbar
   */
  useLinkPickerElementToolbar: {
    source: FeatureFlagSource.Statsig,
    enabled: false,
    flag: 'whiteboards-use-link-picker-element-toolbar',
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Gate for template insertion contextual UI side effects, i.e. open shape main toolbar
   * or sidebar pending underlying variant - confusingly named,
   * as it currently gates the experiments-funnel module
   * https://switcheroo.atlassian.com/ui/gates/3e9f7182-2cbc-4a6b-890f-5b2adfe9ee36/key/whiteboards-contextual-shapes-gate
   */
  whiteboardsContextualShapesGate: {
    source: FeatureFlagSource.Statsig,
    enabled: false,
    flag: 'whiteboards-contextual-shapes-gate',
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables the use of new Rovo mini modal options, which requires canvas to stop handling certain Smart create options.
   * https://switcheroo.atlassian.com/ui/gates/57ad995f-ecbb-4425-b178-374a71d08b48/key/cc-whiteboards-override-rovo-mini-modal-options
   * */
  rovoMiniModalOptionsOverride: {
    source: FeatureFlagSource.Statsig,
    enabled: false,
    flag: 'cc-whiteboards-override-rovo-mini-modal-options',
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables whiteboard context size optimisation to allow large whiteboard context to be edited via AIFC
   * https://switcheroo.atlassian.com/ui/gates/264e9d73-8d29-4c48-bbe5-4d6d53ad453c/key/whiteboards-context-size-optimisation
   * */
  whiteboardContextOptimisation: {
    source: FeatureFlagSource.Statsig,
    enabled: false,
    flag: 'whiteboards-context-size-optimisation',
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables minification of SVG content
   * https://switcheroo.atlassian.com/ui/gates/8b651a50-bea8-40b0-989b-9cf3e02c6026/key/whiteboards_minify_aifc
   */
  minifySVG: {
    source: FeatureFlagSource.Statsig,
    enabled: false,
    flag: 'whiteboards_minify_aifc',
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Kill switch for the Jira Unfurl module
   * https://switcheroo.atlassian.com/ui/gates/a7503ddb-73e9-4525-8254-6bd8c2b9a913/key/cc-whiteboards-jira-unfurl-module
   */
  jiraUnfurlModule: {
    source: FeatureFlagSource.Statsig,
    enabled: false,
    flag: 'cc-whiteboards-jira-unfurl-module',
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables the improved UI/UX for native database embeds
   * https://switcheroo.atlassian.com/ui/gates/7ec3a174-97e4-4aa3-b2bd-0d9038b02e52/key/cc-whiteboards-db-nice
   */
  nativeDbEmbed: {
    source: FeatureFlagSource.Statsig,
    enabled: false,
    flag: 'cc-whiteboards-db-nice',
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Below is the fg check tied to this experiment, which allows default data to be applied to a database on quick insert creation
   * https://switcheroo.atlassian.com/ui/gates/16cd44f6-e160-4106-b581-966fc3ac64fd/key/cc_fd_db_quick_insert_options
   */
  applyDatabaseTemplateFromQuickInsert: {
    source: FeatureFlagSource.Statsig,
    enabled: false,
    flag: 'cc_fd_db_quick_insert_options_fg',
    targetApp: [TargetApp.DatabasesWeb],
  },
  /**
   * Enables the Atlassian architecture icons in the cloud architecture section
   */
  atlassianArchitectureIcons: {
    source: FeatureFlagSource.Statsig,
    enabled: false,
    flag: 'cc-whiteboards-atlassian-architecture-icons',
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables proportional offset for attachments, making stamps maintain relative position when parent is resized
   */
  proportionalOffsetAttachment: {
    enabled: false,
    flag: 'cns-18501-proportional-offset-attachment',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enabled UI/UX refresh for voting feature
   * https://switcheroo.atlassian.com/ui/gates/cb4ccc52-7882-4de1-943a-9c05ce11e032/key/whiteboards-uxhealthdec2025-voting-refresh
   */
  votingRefresh: {
    enabled: false,
    flag: 'whiteboards-uxhealthdec2025-voting-refresh',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enabled path dodging in AIFC
   * https://switcheroo.atlassian.com/ui/gates/1db8edaf-c35e-47e0-a82e-ce0e72bf822b/key/cc-whiteboards-ai-diagramming-path-dodging
   */
  aiPathDodging: {
    enabled: false,
    flag: 'cc-whiteboards-ai-diagramming-path-dodging',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /* Enables edge selection in AI diagramming
   * https://switcheroo.atlassian.com/ui/gates/00d863e2-d16b-4eb2-98fc-bdb3d326ae4f/key/cc-whiteboards-ai-diagramming-edge-selection
   */
  aiEdgeSelection: {
    enabled: false,
    flag: 'cc-whiteboards-ai-diagramming-edge-selection',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enable A11Y changes for databases
   * https://switcheroo.atlassian.com/ui/gates/868c38b1-65b0-4ab8-a00f-a8860bc5d3ad/key/a11y_whiteboards_changes
   */
  a11yWhiteboardsChanges: {
    enabled: false,
    flag: 'a11y_whiteboards_changes',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb, TargetApp.DatabasesWeb],
  },
  /**
   * hot-125716, reconciles orphaned connectors
   * https://switcheroo.atlassian.com/ui/gates/153d2a83-b3e3-4c72-99b8-1b0304faa9f1/key/confluence-whiteboards-fix-orphaned-connectors
   */
  orphanedConnectorReconciliation: {
    enabled: false,
    flag: 'confluence-whiteboards-fix-orphaned-connectors',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables all the features required for the UML shapes to work while the experiment controls specific UI interactions and entry points.
   * https://switcheroo.atlassian.com/ui/gates/23e43173-6e92-47e1-8360-d2248d5cbae1/key/cc-whiteboards-uml-shapes-module
   */
  umlShapesFeatureEngine: {
    enabled: false,
    flag: 'cc-whiteboards-uml-shapes-module',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables the improved copy paste features where pasted text is formatted and pasting text has the paste options toolbar appear.
   * https://switcheroo.atlassian.com/ui/gates/d18df6e5-6497-41e9-b6e1-3623cf8605ff/key/cc-whiteboards-improved-copy-paste-ff
   */
  improvedCopyPasteFlag: {
    enabled: true,
    flag: 'cc-whiteboards-improved-copy-paste-ff',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables all the features required for equidistant spacing to work while the experiment controls specific UI interactions and entry points.
   * https://switcheroo.atlassian.com/ui/gates/48fdacef-b16e-4695-93b0-ec71bde9a9d3/key/cc-whiteboards-equidistant-spacing-module
   */
  equidistantSpacingFeatureEngine: {
    enabled: false,
    flag: 'cc-whiteboards-equidistant-spacing-module',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables using SpotlightCard from @atlaskit/spotlight instead of LegacySpotlightCard
   * Gate must be created in Switcheroo for canvas_web.
   */
  'cns-24597-spotlight-card-migration': {
    enabled: false,
    flag: 'cc-whiteboards-spotlight-card-migration',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  aifcCopyAnalytics: {
    enabled: false,
    flag: 'aifc_copy_analytics',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb, TargetApp.DatabasesWeb],
  },
  /**
   * Enables MathML in Confluence slides (parsing, sanitization, deck math font CSS).
   */
  shplxii657MathSlide: {
    enabled: false,
    flag: 'shplxii-657-math-slide',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables theme propagation to same-origin iframes embedded within the Canvas app
   * (e.g. a whiteboard embedded inside another whiteboard).
   * https://switcheroo.atlassian.com/ui/gates/04e7b05b-7029-46b4-b8c4-cb56fd8384e1/key/confluence_pass_theme_change_to_iframes
   */
  iframeThemingSupport: {
    enabled: false,
    flag: 'confluence_pass_theme_change_to_iframes',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables the parallel v2 agentic pipeline for slides create/edit/offline flows.
   * The flag is intentionally dormant until slides routes v2 at a single boundary.
   */
  slidesAgentV2: {
    enabled: false,
    source: FeatureFlagSource.Local,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Guards upcoming manual editing changes in Confluence slides.
   */
  slidesManualEdit: {
    enabled: false,
    flag: 'cc-mui-slides-manual-edit',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Guards extraction and insertion dragging for Confluence slides nodes.
   */
  slidesNodeDragging: {
    enabled: false,
    flag: 'cc-mui-slides-node-dragging',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Guards the refactor of slides ingestion to mirror AFC ingestion to support MCP for slides.
   * https://switcheroo.atlassian.com/ui/gates/33a300c7-4a6e-4e25-8f23-95800c33ab2d/key/cc-mui-slides-ingestion-refactor
   */
  slidesIngestionRefactor: {
    enabled: false,
    flag: 'cc-mui-slides-ingestion-refactor',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Guards upcoming slide export functionality in Confluence slides.
   * https://switcheroo.atlassian.com/ui/gates/1847b4a0-be5f-4407-a0fe-3c55be553418/key/cc-mui-slides-export
   */
  slidesExport: {
    enabled: false,
    flag: 'cc-mui-slides-export',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },

  /**
   * Guards quality fixes for Confluence slides for the 10% release.
   * https://switcheroo.atlassian.com/ui/gates/f8d42243-10f4-490c-ac73-7650ca71b017/key/cc-mui-slides-quality-10-percent
   */
  slidesQuality10Percent: {
    enabled: false,
    flag: 'cc-mui-slides-quality-10-percent',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables cross-deck copy paste feature for slides
   * https://switcheroo.atlassian.com/ui/gates/062f1a45-119d-4513-b2f4-b05800dee1ff/key/cc_slides_copy_paste
   */
  slidesCopyPaste: {
    enabled: false,
    flag: 'cc_slides_copy_paste',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables the UI to show for the cross flow touchpoint from whiteboards to JPD
   * https://switcheroo.atlassian.com/ui/gates/1faf7547-d0ca-4673-abc3-ffc4dce3ed96/key/jpdgrw-147_whiteboards_to_jpd_killswitch
   */
  jpdWhiteboardsKillSwitch: {
    enabled: false,
    flag: 'jpdgrw-147_whiteboards_to_jpd_killswitch',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables updated AI model versions across whiteboards (AIFC and metadata generation).
   * https://switcheroo.atlassian.com/ui/gates/whiteboards-update-models
   */
  whiteboardsUpdateModels: {
    enabled: false,
    flag: 'whiteboards-update-models',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables grouping exposure to fire from the toolbar eligibility path.
   * https://switcheroo.atlassian.com/ui/gates/whiteboards-grouping-exposure-fixing
   */
  whiteboardsGroupingExposureFixing: {
    enabled: false,
    flag: 'whiteboards-grouping-exposure-fixing',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Migrates the Confluence Object Sidebar (comments and details panels) from a custom floating
   * panel implementation to the @atlassian/panel-system PanelContainer. When enabled, the sidebar
   * is rendered in the ADS navigation-system Panel slot (outside the canvas iframe),
   * which pushes the iframe narrower rather than overlaying it. As a result, the canvas should NOT
   * open its internal CanvasDrawerPanel for external panels (commentsPanel, detailsPanel), and
   * decoration position offsets for the sidebar width are not needed.
   * https://switcheroo.atlassian.com/ui/gates/3974d23d-a3c1-43cb-870d-78c0eb3d496d/key/ads-panel-system-confluence-object-sidebar
   */
  adsPanelSystemConfluenceObjectSidebar: {
    enabled: false,
    flag: 'ads-panel-system-confluence-object-sidebar',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
} as const satisfies Record<string, ClientFeatureFlag>;

type Mutable<T> = { -readonly [P in keyof T]: Mutable<T[P]> };

// Resolved feature flags without overrides applied.
export const resolvedFeatureFlagState: Mutable<ClientFeatureFlags> =
  defaultFeatureFlagState;

/**
 * Initial state is set using the `initializeStatsigFlagState` in statsig.ts.
 */
export const currentFeatureFlagState: Mutable<ClientFeatureFlags> = proxy(
  defaultFeatureFlagState
);

/**
 * If true, supplied feature flag is enabled.
 * @description Ensure you check this value after app boot.
 * @param flag
 * @returns
 */
export const isFeatureFlagEnabled = (flag: ClientFeatureFlagName) => {
  /**
   * The session init handlers are called before we get FF values from statsig,
   * hence using environments to control the behaviour.
   */
  if (flag === 'explicitSPANavigation') {
    return true;
  }

  return Boolean(currentFeatureFlagState[flag].enabled);
};

export const resetFeatureFlagState = () => {
  for (const featureFlag of Object.keys(
    defaultFeatureFlagState
  ) as (keyof typeof defaultFeatureFlagState)[]) {
    currentFeatureFlagState[featureFlag] = defaultFeatureFlagState[featureFlag];
  }
};

export type FeatureFlagOverrides = Record<string, boolean>;

export const setFeatureFlagOverride = (featureFlagOverrides: FeatureFlagOverrides) => {
  for (const flag of Object.keys(defaultFeatureFlagState) as ClientFeatureFlagName[]) {
    currentFeatureFlagState[flag].enabled =
      featureFlagOverrides[flag] ?? defaultFeatureFlagState[flag].enabled;
  }
};
